import {useRef} from 'react';
import PropTypes from 'prop-types';
import {defineMessages, FormattedMessage} from 'react-intl';

import {Button, Space} from 'antd';

import classNames from 'classnames';
import get from 'lodash/get';
import invoke from 'lodash/invoke';

import commonMessages from '@components/Common/CommonMessages/commonMessages';

import NavigationBlocker from '../../Common/NavigationBlocker/NavigationBlocker';
import {getFirstErrorFieldName} from './formFooter.utils';

import './FormFooter.css';

const messages = defineMessages({
  submitText: {
    id: 'FormInlineFooter.submitText',
    defaultMessage: 'Save',
  },
});

/**
 * Form Inline Footer component
 *
 * To be used when as footer component on form pages or part of cards actions
 * includes acceptance and cancel buttons in addition to optional extra button
 */
const FormFooter = ({
  extra,
  loading,
  submitText = <FormattedMessage {...messages.submitText} />,
  cancelText = <FormattedMessage {...commonMessages.cancel} />,
  onValidationError,
  onSubmit,
  onCancel,
  formRef,
  form,
  className,
  disableNavigationCheck,
  disableSubmit,
  discardModalTitle,
  discardModalContent,
  customSubmitButton,
  extraFooterButtons,
}) => {
  const processingSubmitRef = useRef(false);

  const getFormReference = () => {
    return form ? form : get(formRef, ['current']);
  };

  const handleSubmit = () => {
    const formReference = getFormReference();
    let validatePromise = invoke(formReference, ['validateFields']);
    if (validatePromise) {
      validatePromise
        .then((data) => {
          processingSubmitRef.current = true;
          onSubmit?.(data)?.finally(() => {
            processingSubmitRef.current = false;
          });
        })
        .catch((errorInfo) => {
          const firstErrorField = getFirstErrorFieldName(errorInfo);

          invoke(formReference, ['scrollToField'], firstErrorField, {behavior: 'smooth'});

          onValidationError?.(errorInfo);
          console.error(errorInfo);
        });
    }
  };

  const preventNavigation = () => {
    return !disableNavigationCheck && !processingSubmitRef.current && getFormReference()?.isFieldsTouched();
  };

  const onNavigation = () => {
    getFormReference()?.resetFields();
  };

  return (
    <div className={classNames('FormFooter', className)}>
      <NavigationBlocker
        when={preventNavigation}
        onNavigation={onNavigation}
        title={discardModalTitle}
        message={discardModalContent}
      />

      <div>
        {extra && <div className="pull-left">{extra}</div>}

        <Space className="pull-right">
          {onCancel && <Button onClick={onCancel}>{cancelText}</Button>}

          {extraFooterButtons}

          {customSubmitButton ? (
            customSubmitButton
          ) : (
            <Button htmlType="button" type="primary" disabled={disableSubmit} onClick={handleSubmit} loading={loading}>
              {submitText}
            </Button>
          )}
        </Space>

        <div className="clearfix" />
      </div>
    </div>
  );
};

FormFooter.propTypes = {
  extra: PropTypes.element,
  loading: PropTypes.bool,
  submitText: PropTypes.node,
  cancelText: PropTypes.node,
  onValidationError: PropTypes.func,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  formRef: PropTypes.object,
  form: PropTypes.object,
  className: PropTypes.string,
  disableNavigationCheck: PropTypes.bool,
  disableSubmit: PropTypes.bool,
  discardModalTitle: PropTypes.node,
  discardModalContent: PropTypes.node,
  customSubmitButton: PropTypes.node,
  extraFooterButtons: PropTypes.node,
};

export default FormFooter;
